<template>
    <div>
        <div class="flex-h" style="justify-content: space-between; align-items: center; margin-top: 50px;">
            <div class="text-h4">Показания мощности</div>
            <div style="margin-left: 20px;">
                <b>за</b>&nbsp;
                <DatePicker
                    v-model="dateFilterRange"
                    range
                    type="date"
                    :valueType="sqlDateTimeFormat"
                    :format="displayDateFormat"
                    range-separator=" — "
                />
            </div>
            <v-spacer />
        </div>

        <v-tabs
            v-model="tab"
            background-color="grey lighten-3"
            style="margin: 20px 0 20px;"
        >
            <v-tab href="#tab-chart">На графике</v-tab>
            <v-tab href="#tab-list">Список пакетов</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
            <v-tab-item value="tab-chart">
                <chart :options="chartOptions" />
            </v-tab-item>

            <v-tab-item value="tab-list">
                <v-data-table
                    :headers="packetsTableColumns"
                    :items="packetsTableList"
                    class="elevation-1"
                    :items-per-page="-1"
                    hide-default-footer
                    disable-sort
                >
                    <template #item="row">
                        <tr>
                            <td v-for="(column, i) in packetsTableColumns" :key="i">
                                {{ getPacketsTableCellValue(row.item, column) }}
                            </td>
                        </tr>
                    </template>
                </v-data-table>
            </v-tab-item>
        </v-tabs-items>
    </div>
</template>

<script lang="ts">
import Vue from 'vue'
import Component from 'vue-class-component'
import {Inject, Prop, Watch} from 'vue-property-decorator'
import Api from '@/services/Api'
import {Device} from '@/models/Device'
import DeviceBrowser from '@/components/browsers/DeviceBrowser.vue'
import DeviceEditor from '@/components/modals/DeviceEditor.vue'
import {Chart} from 'highcharts-vue'
import {PowerPacket} from '@/models/PowerPacket'
import day from 'dayjs'
import DatePicker from 'vue2-datepicker'
import {
    DISPLAY_DATE_FORMAT,
    displayTime, setMidnight,
    SQL_DATE_TIME_FORMAT,
} from '@/utils/datetime'
import {Options} from 'highcharts/highcharts.src'
import {TableHeader} from '@/components/common/TableHeader'

type PowerPacketColumn = TableHeader<PowerPacket>

@Component({
    components: {Chart, DeviceEditor, DeviceBrowser, DatePicker},
})
export default class PowerPacketsBlock extends Vue
{
    @Inject('api') private api!: Api

    @Prop({required: true, type: Object})
    device!: Device

    sqlDateTimeFormat = SQL_DATE_TIME_FORMAT
    displayDateFormat = DISPLAY_DATE_FORMAT

    packets: PowerPacket[] = []

    receivedSince: string | null = null
    receivedTill: string | null = null

    tab = 'chart'

    packetsTableColumns: PowerPacketColumn[] = [
        {text: 'ID', value: 'id', align: 'start'},
        {text: 'Получено в', value: packet => displayTime(packet.receivedAt)},
        {text: 'Мощность', value: packet => packet.power + ' Вт'},
    ]

    get packetsTableList() {
        return this.packets
    }

    getPacketsTableCellValue(packet: PowerPacket, column: PowerPacketColumn): string {
        if (column.value == null) {
            return ''
        }
        return column.value instanceof Function ? column.value(packet) : (packet[column.value] + '')
    }

    get chartOptions(): Options {
        return {
            series: [{
                name: 'Мощность',
                type: 'line',
                data: [...this.packets].reverse().map(packet => [day.utc(packet.receivedAt, this.sqlDateTimeFormat).unix() * 1000, packet.power]),
                tooltip: {
                    pointFormat: '{series.name}: <b>{point.y}Вт</b><br/>',
                },
                step: 'left',
            }],
            credits: {
                enabled: false,
            },
            title: {
                text: '',
            },
            xAxis: {
                type: 'datetime',
            },
            yAxis: {
                title: {
                    text: '',
                },
                labels: {
                    formatter: ctx => ctx.value + 'Вт',
                },
            },
            chart: {
                zooming: {
                    mouseWheel: {
                        type: 'x',
                    },
                },
                panning: {
                    enabled: true,
                },
            },
            time: {
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
        }
    }

    async created() {
        this.receivedSince = setMidnight(day().subtract(7, 'day')).format(this.sqlDateTimeFormat)
        this.receivedTill = setMidnight(day()).format(this.sqlDateTimeFormat)

        if (this.device) {
            await this.loadPackets()
        }
    }

    async loadPackets() {
        this.packets = await this.fetchPackets()
    }

    get dateFilterRange(): [string, string] {
        return [this.receivedSince || '', this.receivedTill || '']
    }

    // noinspection JSUnusedGlobalSymbols
    set dateFilterRange([since, till]: [string, string]) {
        this.receivedSince = since
        this.receivedTill = till
    }

    @Watch('receivedSince')
    onReceivedSinceChanged() {
        this.loadPackets()
    }

    @Watch('receivedTill')
    onReceivedTillChanged() {
        this.loadPackets()
    }

    async fetchPackets() {
        return await this.api.listPowerPackets({
            deviceId: this.device.id,
            receivedSince: this.receivedSince ? day(this.receivedSince, this.sqlDateTimeFormat).utc().format(this.sqlDateTimeFormat) : null,
            receivedTill: this.receivedTill ? day(this.receivedTill, this.sqlDateTimeFormat).utc().add(1, 'day').format(this.sqlDateTimeFormat) : null,
        })
    }
}
</script>
